import { AxiosError } from 'axios';
import useSWRMutation from 'swr/mutation';

import {
  camelizeKeys,
  isDefined,
  MaritalStatusValueT,
  marriedStatuses,
  RegistrationAddressT,
  RegistrationDataT,
  WithStatusT,
} from '@consumer-frontend/nc-utils';

import {
  apiEndpoints,
  unauthenticatedFetchers,
} from '../lib';

export type BuildRegisterRequestParamsT = {
  registrationData: Partial<RegistrationDataT>;
};

export type RegisterV2RequestT = {
  apt_or_unit?: string;
  city?: string;
  date_of_birth?: string; // ISO 8601 date
  first_name?: string;
  home_address?: string;
  last_name?: string;
  main_phone?: string; // valid phone format
  marital_status?: MaritalStatusValueT;
  postal_code?: string;
  spouse_apt_or_unit?: string;
  spouse_city?: string;
  spouse_first_name?: string;
  spouse_home_address?: string;
  spouse_last_name?: string;
  spouse_postal_code?: string;
  spouse_state?: string;
  state?: string;
};

export type RawRegisterV2ResponseT = {
  access_token: string;
  account_id: string;
  refresh_token: string;
};

export type RegisterV2ResponseT = {
  accessToken: string;
  accountId: string;
  refreshToken: string;
  status: number; // 200 = account updated, 201 = account created
};

export type RegisterV2ErrorTypeT = 'CACHE_MISS' | 'email_not_verified';

export type RegisterV2ErrorResponseT = {
  error_type?: RegisterV2ErrorTypeT;
};

export const buildRegisterRequest = ({
  registrationData,
}: BuildRegisterRequestParamsT): RegisterV2RequestT => {
  const { address, maritalInformation } = registrationData;
  const spouse = maritalInformation?.spouse;
  const spouseAddress: Partial<RegistrationAddressT> = (spouse?.sameAddress ? address : spouse?.address) || {};
  const includeSpouseInfo = isDefined(maritalInformation)
    && marriedStatuses.includes(maritalInformation.maritalStatus);

  return {
    apt_or_unit: address?.addressLine2,
    city: address?.city || '',
    date_of_birth: registrationData.dateOfBirth || '',
    first_name: registrationData.firstName || '',
    home_address: address?.addressLine1 || '',
    last_name: registrationData.lastName || '',
    main_phone: registrationData.phoneNumber || '',
    marital_status: maritalInformation?.maritalStatus,
    postal_code: address?.postalCode || '',
    state: address?.state || '',

    ...(includeSpouseInfo && {
      spouse_apt_or_unit: spouseAddress?.addressLine2 || '',
      spouse_city: spouseAddress?.city || '',
      spouse_first_name: spouse?.firstName || '',
      spouse_home_address: spouseAddress?.addressLine1 || '',
      spouse_last_name: spouse?.lastName || '',
      spouse_postal_code: spouseAddress?.postalCode || '',
      spouse_state: spouseAddress?.state || '',
    }),
  };
};

export const cleanUpRegistrationData = (registrationData: Partial<RegistrationDataT>): Partial<RegistrationDataT> => {
  const { address } = registrationData;

  return {
    address: address
      ? {
        addressLine1: '',
        addressLine2: '',
        city: '',
        postalCode: '',
        state: address.state,
      }
      : undefined,
    loanAmount: registrationData.loanAmount,
    loanPurpose: registrationData.loanPurpose,
  };
};

export const useRegisterV2 = (throwOnError = false) => {
  const {
    error,
    isMutating,
    reset,
    trigger: registerV2,
  } = useSWRMutation<
    RegisterV2ResponseT,
    AxiosError<RegisterV2ErrorResponseT>,
    string,
    RegisterV2RequestT
  >(
    apiEndpoints.apply.v2.register,
    async (url, { arg }) => {
      // eslint-disable-next-line max-len
      const data = await unauthenticatedFetchers.post<RegisterV2RequestT>(url, { arg }) as WithStatusT<RawRegisterV2ResponseT>;

      const camelizedData = camelizeKeys(data) as RegisterV2ResponseT;

      return camelizedData;
    },

    { throwOnError },
  );

  return {
    error,
    isMutating,
    registerV2,
    reset,
  };
};
